import type { MaybeRefOrGetter } from 'vue';

import { computed, readonly, ref, toValue } from 'vue';

interface CursorPageInfo {
  endCursor?: string | null;
  hasNextPage?: boolean | null;
}

type FetchMoreAfterCursor = (options: { variables: { after: string } }) => unknown;

interface CursorLoadMoreOptions {
  /** Метка включает dev-диагностику исправленного pagination flow с префиксом `[FIX]`. */
  debugLabel?: string;
}

/**
 * Подгрузка следующей страницы Relay-соединения для infinity scroll в BaseTable и AppLoadMore.
 *
 * Отдаёт `fetchingMore` для проброса в таблицу и `loadMore` для `@fetch-more` / `@load`.
 * Повторные вызовы во время загрузки и вызовы без курсора игнорируются.
 *
 * @example
 * ```typescript
 * const pageInfo = computed(() => displayData.value?.platformUsers.pageInfo);
 * const { fetchingMore, loadMore } = useCursorLoadMore(pageInfo, fetchMore, { debugLabel: 'platform-users' });
 * ```
 */
export function useCursorLoadMore(
  pageInfo: MaybeRefOrGetter<CursorPageInfo | null | undefined>,
  fetchMore: FetchMoreAfterCursor,
  options: CursorLoadMoreOptions = {},
) {
  const fetchingMore = ref(false);
  const debugLabel = options.debugLabel?.trim();
  const shouldLogFix = import.meta.env.DEV && Boolean(debugLabel);

  const loadMore = async (): Promise<void> => {
    const currentPageInfo = toValue(pageInfo);
    const after = currentPageInfo?.endCursor;

    if (!after || currentPageInfo.hasNextPage === false || fetchingMore.value) {
      return;
    }

    fetchingMore.value = true;

    if (shouldLogFix) {
      // eslint-disable-next-line no-console -- Временная dev-диагностика исправленного infinity-scroll flow.
      console.debug('[FIX:cursor-pagination] Input', { label: debugLabel, after });
    }

    try {
      await fetchMore({ variables: { after } });
      if (shouldLogFix) {
        // eslint-disable-next-line no-console -- Временная dev-диагностика исправленного infinity-scroll flow.
        console.debug('[FIX:cursor-pagination] Success', { label: debugLabel, after });
      }
    } catch (error) {
      if (shouldLogFix) {
        console.error('[FIX:cursor-pagination] Error', { label: debugLabel, after, error });
      }
      throw error;
    } finally {
      fetchingMore.value = false;
    }
  };

  return {
    fetchingMore: readonly(fetchingMore),
    hasMore: computed(() => Boolean(toValue(pageInfo)?.hasNextPage)),
    loadMore,
  };
}
